import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Smartphone, Tablet, Monitor, Play, RotateCcw,
  CheckCircle, ArrowRight, Sparkles, MessageSquare,
  Heart, Target, Users, Zap, Star
} from 'lucide-react';
import { 
  MobileChoiceSelector,
  MobileComparisonSelector, 
  PACEMobileBuilder,
  ResponsiveLessonContainer,
  TouchFriendlyMaya,
  AIOutputComparison,
  CHOICE_LIBRARIES,
  MOBILE_PATTERNS,
  type MobileSelectConfig
} from './index';

interface DemoSection {
  id: string;
  title: string;
  description: string;
  component: React.ReactNode;
  features: string[];
}

export const MobileUXDemo: React.FC = () => {
  const [activeDemo, setActiveDemo] = useState<string>('overview');
  const [devicePreview, setDevicePreview] = useState<'mobile' | 'tablet' | 'desktop'>('mobile');
  const [demoResults, setDemoResults] = useState<Record<string, any>>({});

  const demoSections: DemoSection[] = [
    {
      id: 'choice-selector',
      title: 'Mobile Choice Selector',
      description: 'Replace text inputs with touch-friendly choice selection',
      features: ['Tap to select', 'Multi-select support', 'Icon integration', 'Responsive layouts'],
      component: <ChoiceSelectorDemo onResult={(data) => setDemoResults(prev => ({ ...prev, choice: data }))} />
    },
    {
      id: 'ai-comparison',
      title: 'AI Output Comparison',
      description: 'Side-by-side comparison with tap selection and reasoning',
      features: ['Visual comparison', 'Reasoning selection', 'Metrics display', 'Mobile-optimized'],
      component: <AIComparisonDemo onResult={(data) => setDemoResults(prev => ({ ...prev, comparison: data }))} />
    },
    {
      id: 'prompt-builder',
      title: 'Mobile Prompt Builder',
      description: 'Template-based prompt creation with choice selectors',
      features: ['Template library', 'Choice-based variables', 'PACE framework', 'Progressive disclosure'],
      component: <PromptBuilderDemo onResult={(data) => setDemoResults(prev => ({ ...prev, prompt: data }))} />
    },
    {
      id: 'maya-integration',
      title: 'Interactive Maya Story',
      description: 'Story-driven learning with choice-based decisions',
      features: ['Story progression', 'Decision impacts', 'Character development', 'Learning outcomes'],
      component: <MayaStoryDemo onResult={(data) => setDemoResults(prev => ({ ...prev, maya: data }))} />
    },
    {
      id: 'responsive-lesson',
      title: 'Responsive Lesson Container',
      description: 'Adaptive lesson layout that responds to device size',
      features: ['Device detection', 'Adaptive layouts', 'Progress tracking', 'Interaction variety'],
      component: <ResponsiveLessonDemo onResult={(data) => setDemoResults(prev => ({ ...prev, lesson: data }))} />
    }
  ];

  const getDeviceIcon = (device: string) => {
    switch (device) {
      case 'mobile': return <Smartphone className="w-4 h-4" />;
      case 'tablet': return <Tablet className="w-4 h-4" />;
      case 'desktop': return <Monitor className="w-4 h-4" />;
      default: return <Smartphone className="w-4 h-4" />;
    }
  };

  const getPreviewClass = () => {
    switch (devicePreview) {
      case 'mobile': return 'max-w-sm mx-auto';
      case 'tablet': return 'max-w-2xl mx-auto';
      case 'desktop': return 'max-w-6xl mx-auto';
      default: return 'max-w-sm mx-auto';
    }
  };

  if (activeDemo === 'overview') {
    return (
      <div className="w-full max-w-6xl mx-auto space-y-8 p-6">
        {/* Header */}
        <div className="text-center">
          <motion.div
            className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 300 }}
          >
            <Sparkles className="w-10 h-10 text-white" />
          </motion.div>
          <h1 className="text-3xl font-bold mb-4">Mobile-First UX Component Library</h1>
          <p className="text-gray-600 text-lg mb-6 max-w-2xl mx-auto">
            Transform text inputs into touch-friendly, choice-based interactions that optimize learning for mobile devices.
          </p>
          
          {/* Device Preview Toggle */}
          <div className="flex justify-center mb-8">
            <div className="flex bg-gray-100 rounded-lg p-1">
              {(['mobile', 'tablet', 'desktop'] as const).map(device => (
                <button
                  key={device}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md transition-all ${
                    devicePreview === device
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  onClick={() => setDevicePreview(device)}
                >
                  {getDeviceIcon(device)}
                  <span className="capitalize text-sm font-medium">{device}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Component Showcase */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {demoSections.map((section, index) => (
            <motion.div
              key={section.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className="cursor-pointer hover:shadow-lg transition-all h-full">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center justify-between">
                    {section.title}
                    {demoResults[section.id.split('-')[0]] && (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    )}
                  </CardTitle>
                  <p className="text-sm text-gray-600">{section.description}</p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex flex-wrap gap-1">
                      {section.features.map(feature => (
                        <Badge key={feature} variant="outline" className="text-xs">
                          {feature}
                        </Badge>
                      ))}
                    </div>
                    <Button 
                      onClick={() => setActiveDemo(section.id)}
                      className="w-full"
                      size="sm"
                    >
                      <Play className="w-4 h-4 mr-2" />
                      Try Demo
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Design Principles */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-8">
          <h2 className="text-2xl font-bold mb-6 text-center">Mobile-First Design Principles</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: <Target className="w-8 h-8 text-blue-600" />,
                title: 'Choice Over Text',
                description: 'Replace text inputs with selectable options and pre-written choices'
              },
              {
                icon: <Heart className="w-8 h-8 text-pink-600" />,
                title: 'Touch-Friendly',
                description: '44px+ touch targets with comfortable spacing for thumb navigation'
              },
              {
                icon: <Zap className="w-8 h-8 text-yellow-600" />,
                title: 'Progressive Disclosure',
                description: 'Show information gradually to prevent overwhelming mobile users'
              },
              {
                icon: <Users className="w-8 h-8 text-green-600" />,
                title: 'Decision-Based Learning',
                description: 'Learn through making choices and comparing different options'
              }
            ].map((principle, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                className="text-center"
              >
                <div className="mb-4">{principle.icon}</div>
                <h3 className="font-semibold mb-2">{principle.title}</h3>
                <p className="text-sm text-gray-600">{principle.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const currentSection = demoSections.find(section => section.id === activeDemo);

  return (
    <div className={`w-full ${getPreviewClass()} space-y-6 p-4`}>
      {/* Demo Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => setActiveDemo('overview')}>
            ← Back to Overview
          </Button>
          <div>
            <h2 className="text-xl font-semibold">{currentSection?.title}</h2>
            <p className="text-sm text-gray-600">{currentSection?.description}</p>
          </div>
        </div>
        
        {/* Device Preview Toggle */}
        <div className="flex bg-gray-100 rounded-lg p-1">
          {(['mobile', 'tablet', 'desktop'] as const).map(device => (
            <button
              key={device}
              className={`p-2 rounded-md transition-all ${
                devicePreview === device
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              onClick={() => setDevicePreview(device)}
            >
              {getDeviceIcon(device)}
            </button>
          ))}
        </div>
      </div>

      {/* Demo Component */}
      <div className="border rounded-lg p-6 bg-white">
        <AnimatePresence mode="wait">
          <motion.div
            key={`${activeDemo}-${devicePreview}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            {currentSection?.component}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Reset Button */}
      <div className="flex justify-center">
        <Button 
          variant="outline" 
          onClick={() => setDemoResults({})}
          className="flex items-center gap-2"
        >
          <RotateCcw className="w-4 h-4" />
          Reset Demo
        </Button>
      </div>
    </div>
  );
};

// Individual demo components
const ChoiceSelectorDemo: React.FC<{ onResult: (data: any) => void }> = ({ onResult }) => {
  const config: MobileSelectConfig = {
    title: 'Choose Your Communication Style',
    description: 'Select the tone that best fits your message',
    options: CHOICE_LIBRARIES.communicationTones,
    multiSelect: false,
    showIcons: true,
    layout: 'grid'
  };

  return (
    <MobileChoiceSelector
      config={config}
      selected={[]}
      onSelectionChange={(selected) => onResult({ selected, timestamp: Date.now() })}
    />
  );
};

const AIComparisonDemo: React.FC<{ onResult: (data: any) => void }> = ({ onResult }) => {
  const mockOutputs = [
    {
      id: 'output1',
      title: 'Direct Approach',
      content: 'Hi team, we need to discuss the project delays and figure out next steps.',
      rating: 3,
      highlights: ['Clear', 'Direct']
    },
    {
      id: 'output2', 
      title: 'Empathetic Approach',
      content: 'Hi everyone, I know we\'re all feeling the pressure from the project delays. Let\'s come together to brainstorm solutions and support each other through this challenge.',
      rating: 5,
      highlights: ['Empathetic', 'Solution-focused', 'Team-oriented']
    }
  ];

  return (
    <MobileComparisonSelector
      title="Which message approach is better?"
      options={mockOutputs}
      onSelection={(selected) => onResult({ selected, timestamp: Date.now() })}
      showRatings={true}
    />
  );
};

const PromptBuilderDemo: React.FC<{ onResult: (data: any) => void }> = ({ onResult }) => {
  return (
    <PACEMobileBuilder
      onComplete={(framework) => onResult({ framework, timestamp: Date.now() })}
    />
  );
};

const MayaStoryDemo: React.FC<{ onResult: (data: any) => void }> = ({ onResult }) => {
  return (
    <TouchFriendlyMaya
      scenarioId="board-email"
      onComplete={(result) => onResult({ result, timestamp: Date.now() })}
    />
  );
};

const ResponsiveLessonDemo: React.FC<{ onResult: (data: any) => void }> = ({ onResult }) => {
  const interactions = [
    {
      id: 'choice1',
      type: 'choice' as const,
      title: 'Communication Style',
      data: {
        title: 'Select Your Approach',
        question: 'How do you prefer to communicate with your team?',
        options: [
          { id: 'direct', label: 'Direct & Clear', icon: <Target className="w-4 h-4" /> },
          { id: 'collaborative', label: 'Collaborative', icon: <Users className="w-4 h-4" /> },
          { id: 'supportive', label: 'Supportive', icon: <Heart className="w-4 h-4" /> }
        ]
      }
    },
    {
      id: 'comparison1',
      type: 'comparison' as const,
      title: 'Message Comparison',
      data: {
        title: 'Which message is more effective?',
        options: [
          { id: 'msg1', title: 'Message A', content: 'Quick update on project status...', rating: 3 },
          { id: 'msg2', title: 'Message B', content: 'Comprehensive project update with detailed analysis...', rating: 4 }
        ]
      }
    }
  ];

  return (
    <ResponsiveLessonContainer
      title="Mobile Communication Mastery"
      content={
        <div>
          <h3 className="font-semibold mb-2">Welcome to Mobile Learning</h3>
          <p className="text-gray-600 text-sm">
            This lesson adapts to your device size and uses choice-based interactions 
            to help you learn communication skills more effectively.
          </p>
        </div>
      }
      interactions={interactions}
      onProgress={(step, data) => onResult({ step, data, timestamp: Date.now() })}
    />
  );
};

export default MobileUXDemo;